import dayjs, { Dayjs } from 'dayjs'
import type { Teaser } from '@/types/rahmen/sitecore-teaser'
import type { MonetaryAmount } from 'policies-api'
import { n } from '@/i18n'
import type {
  ServiceLink,
  ServiceLinkContext,
  ServiceLinkFilterRule
} from '@/types/policy/PolicyTypes'
import { isDa, isTrue } from '@/utils/CommonUtils'

export function getBasePath(): string {
  return import.meta.env.VITE_BASE_PATH
}

export function isDev(): boolean {
  return (
    location.origin.includes('webuidev.azureedge.net') ||
    location.origin.includes('webuisit.azureedge.net')
  )
}

export function isLocal(): boolean {
  return location.origin.includes('localhost')
}

export function getQueryParameterKey(): string {
  const urlParams = new URLSearchParams(globalThis.location.search)
  const items = [
    'bankdaten-aendern',
    'adresse-aendern',
    'name-aendern',
    isDa() ? 'digitale-schadenkarte-anfordern' : 'digitale-versicherungskarte-anfordern',
    'vertrag-aendern',
    'fahrzeug-wechseln',
    'beitragsbescheinigung-anfordern',
    'bescheinigung-fuer-begleitetes-fahren-anfordern',
    'internationale-versicherungskarte-anfordern',
    'notfound'
  ]
  for (const item of items) {
    const value = urlParams.get(item)
    if (value === '') {
      return item
    }
  }
  return 'notfound'
}

export function sanitizeHTML(str: string) {
  return str.replaceAll(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '')
}

export function generateUUID() {
  if (crypto.randomUUID) {
    return crypto.randomUUID()
  } else {
    // Fallback: Manuelle Implementierung mit crypto.getRandomValues
    const arr = new Uint8Array(16)
    crypto.getRandomValues(arr)
    if (arr[6] !== undefined && arr[8] !== undefined) {
      arr[6] = (arr[6] & 0x0f) | 0x40
      arr[8] = (arr[8] & 0x3f) | 0x80
    }

    return [...arr]
      .map(
        (b, i) =>
          (i === 4 || i === 6 || i === 8 || i === 10 ? '-' : '') +
          b.toString(16).padStart(2, '0')
      )
      .join('')
  }
}

export function formatIsoDateString(isoDate: string): string {
  return isoDate.length === 10
    ? `${isoDate.substring(8, 10)}.${isoDate.substring(5, 7)}.${isoDate.substring(0, 4)}`
    : ''
}

export function getCookie(name: string): string | null {
  const value = `; ${document.cookie}`
  const parts = value.split(`; ${name}=`)
  if (parts.length === 2) return parts.pop()?.split(';')?.shift() ?? ''
  else return null
}

const STANDALONE_STORAGE_KEY = 'standalone'

/**
 * Ermittelt den Standalone-Testmodus reload- und domain-stabil über `localStorage`.
 *
 * Ablauf:
 * 1. Ist der Query-Parameter `?standalone=true` bzw. `?standalone=false` gesetzt,
 *    wird der Wert einmalig in `localStorage` persistiert (überschreibt einen evtl.
 *    vorherigen Wert).
 * 2. Andernfalls (oder danach) wird der zuletzt gespeicherte Wert aus `localStorage`
 *    gelesen und zurückgegeben.
 *
 * Vorteil gegenüber Cookies: `localStorage` läuft rein client-seitig, ist nicht von
 * Cookie-Attributen (`Secure`, `SameSite`, `Domain`, `Path`) oder vorgeschalteten
 * Proxies/WAFs (z. B. Incapsula) betroffen und bleibt über Reloads/Neustarts erhalten.
 */
export function isStandaloneMode(): boolean {
  const isStandaloneAllowed = isTrue(
    globalThis.ENV_CONFIG?.FEATURE_FLAGS?.IS_STANDALONE_ALLOWED ?? false
  )

  if (!isStandaloneAllowed) {
    try {
      globalThis.localStorage.removeItem(STANDALONE_STORAGE_KEY)
    } catch {
      // localStorage evtl. nicht verfügbar - Standalone bleibt deaktiviert
    }
    return false
  }

  const urlParams = new URLSearchParams(globalThis.location.search)
  const queryValue = urlParams.get('standalone')

  if (queryValue === 'true' || queryValue === 'false') {
    try {
      globalThis.localStorage.setItem(STANDALONE_STORAGE_KEY, queryValue)
    } catch {
      // localStorage evtl. nicht verfügbar (z. B. Private Mode) - Query-Wert trotzdem nutzen
      return queryValue === 'true'
    }
  }

  try {
    return globalThis.localStorage.getItem(STANDALONE_STORAGE_KEY) === 'true'
  } catch {
    return false
  }
}

export const getDateFormatISO8601 = (date: string): string => {
  const momentDate: Dayjs = dayjs(date, 'DD.MM.YYYY', true)
  if (momentDate.isValid()) {
    return momentDate.format('YYYY-MM-DD')
  }
  return ''
}

export function hexToRgb(hex: string) {
  // Remove the hash if present
  hex = hex.replace(/^#/, '')

  // Parse into R, G, and B components
  const bigint = Number.parseInt(hex, 16)
  const r = (bigint >> 16) & 255
  const g = (bigint >> 8) & 255
  const b = bigint & 255

  return [r, g, b]
}

export function getLuminance(r: number, g: number, b: number) {
  // Normalize the RGB values to [0, 1]
  let rs = r / 255
  let gs = g / 255
  let bs = b / 255

  // Apply gamma correction (sRGB)
  rs = rs <= 0.03928 ? rs / 12.92 : Math.pow((rs + 0.055) / 1.055, 2.4)
  gs = gs <= 0.03928 ? gs / 12.92 : Math.pow((gs + 0.055) / 1.055, 2.4)
  bs = bs <= 0.03928 ? bs / 12.92 : Math.pow((bs + 0.055) / 1.055, 2.4)

  // Calculate luminance
  return 0.2126 * rs + 0.7152 * gs + 0.0722 * bs
}

export function isDark(hexColor: string) {
  if (!hexColor || !/^#?[0-9A-Fa-f]{6}$/.test(hexColor)) return false
  const [r, g, b] = hexToRgb(hexColor)
  const luminance = getLuminance(r!, g!, b!)
  // If luminance is below 0.5, consider it "dark"
  return luminance < 0.5
}

export const useResponsiveClassByElementSize = (width: number) => {
  if (!width) return 'sm'
  let responsiveClass = 'xs'
  if (width < 600 && width > 427) {
    responsiveClass = 'sm'
  } else if (width >= 600 && width < 800) {
    responsiveClass = 'md'
  } else if (width >= 800) {
    responsiveClass = 'lg'
  }
  return responsiveClass
}

export function isSitecoreTeaser(teaser: unknown): teaser is Teaser {
  if (!teaser || typeof teaser !== 'object') return false
  const candidate = teaser as Record<string, unknown>
  if (
    !('image' in candidate) ||
    !('content' in candidate) ||
    !('buttons' in candidate) ||
    !('settings' in candidate)
  ) {
    return false
  }

  const image = candidate.image as Record<string, unknown>
  const content = candidate.content as Record<string, unknown>
  const settings = candidate.settings as Record<string, unknown>
  const buttons = candidate.buttons

  return (
    typeof image.url === 'string' &&
    typeof image.tag === 'string' &&
    typeof image.backgroundColor === 'string' &&
    typeof image.alt === 'string' &&
    typeof content.headline === 'string' &&
    typeof content.text === 'string' &&
    typeof content.backgroundColor === 'string' &&
    Array.isArray(buttons) &&
    buttons.every((button: unknown) => {
      const b = button as Record<string, unknown>
      return (
        typeof b.url === 'string' &&
        typeof b.label === 'string' &&
        typeof b.target === 'string' &&
        typeof b.color === 'string'
      )
    }) &&
    typeof settings.isAdvertisement === 'boolean' &&
    typeof settings.hide === 'boolean' &&
    typeof settings.teaserCentered === 'boolean'
  )
}

export function formatMonetaryValue(
  monetaryValue: MonetaryAmount | undefined | null
): string {
  if (monetaryValue?.amount == null || !monetaryValue?.currency) return ''

  return n(monetaryValue.amount, {
    key: 'currency',
    currency: monetaryValue.currency,
    locale: 'de-DE'
  })
}

/**
 * Speichert key und value in einen LaxCookie mit domain .zurich.com
 * @param key
 * @param value
 * @param domain
 */
export function saveToLaxCookie(
  key: string,
  value: string | null | undefined,
  domain: string
): void {
  // Nur echte Strings (keine null/undefined) und nicht-leere Strings speichern
  if (typeof value === 'string' && value.trim() !== '') {
    document.cookie = `${key}=${value}; path=/; domain=${domain}; Secure; SameSite=Lax`
  }
}

/**
 * Speichert Claims-Daten (Tokens, PPID, ZPartnerId) im LaxCookie.
 * Führt vorher ein Token-Update durch.
 */
export async function storeClaimsData(mandant: 'DA' | 'ZUR'): Promise<void> {
  if (!globalThis.keycloak) {
    console.warn('Keycloak ist nicht initialisiert.')
    return
  }
  try {
    await globalThis.keycloak.updateToken(30) // 30 Sekunden Puffer

    const domainMap: Record<typeof mandant, string[]> = {
      DA: ['.da-direkt.de'],
      ZUR: ['.zurich.com', '.zurich.de']
    }

    const domains = domainMap[mandant]
    domains.forEach((domain) => {
      saveToLaxCookie('kupo.accessToken', globalThis.keycloak.token, domain)
      saveToLaxCookie('kupo.refreshToken', globalThis.keycloak.refreshToken, domain)
      saveToLaxCookie(
        'kupo.keycloakUrl',
        globalThis.ENV_CONFIG?.KEYCLOAK?.BASE_URL
          ? globalThis.ENV_CONFIG.KEYCLOAK.BASE_URL + '/auth'
          : undefined,
        domain
      )
      saveToLaxCookie('kupo.realm', globalThis.ENV_CONFIG?.KEYCLOAK?.REALM, domain)
    })
  } catch (error) {
    console.error('Fehler beim Speichern der Claims-Daten in LaxCookie:', error)
  }
}

export function filterHiddenServiceLinks(serviceLinks: ServiceLink[] | undefined) {
  if (!serviceLinks) return []
  return serviceLinks.filter((serviceLink) => !isTrue(serviceLink.hide ?? false))
}

function matchesDDSLines(
  rule: ServiceLinkFilterRule,
  context: ServiceLinkContext
): boolean {
  if (rule.allowedDDSLines.includes('*')) return true
  return rule.allowedDDSLines.some((line) => context.ddsLines.includes(line))
}

function matchesStatus(
  rule: ServiceLinkFilterRule,
  context: ServiceLinkContext
): boolean {
  if (rule.allowedStatus.includes('*')) return true
  return rule.allowedStatus.includes(context.policyStatus)
}

function matchesCanceledSince(
  rule: ServiceLinkFilterRule,
  context: ServiceLinkContext
): boolean {
  if (rule.canceledSinceInDays <= 0 || context.policyStatus !== 'CANCELED') return true
  if (!context.expiryDatePolicy) return false
  const daysSinceExpiry = dayjs().diff(dayjs(context.expiryDatePolicy), 'day')
  return daysSinceExpiry <= rule.canceledSinceInDays
}

function matchesWKZ(rule: ServiceLinkFilterRule, context: ServiceLinkContext): boolean {
  if (context.wkz === undefined) return true
  if (
    !rule.allowedWKZ.includes('*') &&
    !rule.allowedWKZ.some((prefix) => context.wkz!.startsWith(prefix))
  ) {
    return false
  }
  return !rule.excludedWKZ?.some((prefix) => context.wkz!.startsWith(prefix))
}

function resolveServiceLinkHref(rule: ServiceLinkFilterRule): string | undefined {
  return (
    globalThis.ENV_CONFIG?.SERVICE_LINKS as unknown as Record<string, string | undefined>
  )[rule.hrefConfigKey]
}

function matchesRule(rule: ServiceLinkFilterRule, context: ServiceLinkContext): boolean {
  return (
    matchesDDSLines(rule, context) &&
    !(context.isRedCoop && !rule.allowedRedCoop) &&
    matchesStatus(rule, context) &&
    matchesCanceledSince(rule, context) &&
    matchesWKZ(rule, context)
  )
}

/**
 * Löst Service-Links aus einem regelbasierten System auf.
 *
 * Die Funktion iteriert über die übergebenen Regeln und prüft jede Regel gegen den Vertragskontext.
 * Nur Regeln, die ALLE Bedingungen erfüllen, erzeugen einen Service-Link im Ergebnis.
 * Die `href`-URL wird über `globalThis.ENV_CONFIG.SERVICE_LINKS[rule.hrefConfigKey]` aufgelöst.
 *
 * **Prüfreihenfolge:**
 * 1. DDS-Lines: Mindestens eine `allowedDDSLines`-Eintrag muss in `context.ddsLines` enthalten sein
 *    (`'*'` als einziger Eintrag überspringt diesen Check).
 * 2. RedCoop: Wenn `context.isRedCoop === true` und `rule.allowedRedCoop === false`, wird die Regel
 *    übersprungen.
 * 3. Status: `context.policyStatus` muss in `rule.allowedStatus` enthalten sein
 *    (`'*'` als einziger Eintrag überspringt diesen Check).
 * 4. canceledSinceInDays: Wird nur ausgewertet, wenn `rule.canceledSinceInDays > 0` UND
 *    `context.policyStatus === 'CANCELED'`. Dann muss `expiryDatePolicy` vorhanden sein und
 *    darf maximal `canceledSinceInDays` Tage in der Vergangenheit liegen.
 * 5. WKZ: Wird nur geprüft, wenn `context.wkz` gesetzt ist (für SHUR/Life ist `wkz` undefined →
 *    WKZ-Check wird übersprungen). `context.wkz` muss mit mindestens einem `allowedWKZ`-Präfix
 *    beginnen (`'*'` als einziger Eintrag überspringt diesen Check). Anschließend wird geprüft,
 *    ob `context.wkz` mit einem `excludedWKZ`-Präfix beginnt – falls ja, wird die Regel übersprungen.
 *    Nützlich für Negativbedingungen wie „alle WKZ außer 01x, 03x" (z. B. Kilometerstand-Link).
 * 6. href-Auflösung: `hrefConfigKey` wird in `ENV_CONFIG.SERVICE_LINKS` nachgeschlagen.
 *    Fehlt der Eintrag, wird die Regel übersprungen.
 * 7. Deduplizierung: Regeln mit gleichem `linkname` werden nur einmalig in das Ergebnis aufgenommen
 *    (first-match-wins). Regeln früher im Array haben Vorrang.
 *
 * Die Regeln selbst werden tenant-spezifisch in `@/utilsTenant/serviceLinkFilterRules.ts` definiert.
 *
 * @param rules - Regelarray aus der tenant-spezifischen Konfiguration
 * @param context - Kontext des aktuellen Vertrags (Status, WKZ, DDS-Lines, etc.)
 * @returns Gefilterte und aufgelöste {@link ServiceLink}-Liste
 */
export function resolveServiceLinksFromRules(
  rules: ServiceLinkFilterRule[],
  context: ServiceLinkContext
): ServiceLink[] {
  const result: ServiceLink[] = []
  const seenLinknames = new Set<string>()

  for (const rule of rules) {
    if (!matchesRule(rule, context)) continue

    const href = resolveServiceLinkHref(rule)
    if (!href) continue

    // Deduplizieren: Gleicher linkname → erste Regel gewinnt
    if (seenLinknames.has(rule.linkname)) continue
    seenLinknames.add(rule.linkname)

    result.push({ name: rule.linkname, href })
  }

  return result
}

export function sanitizeForLog(input: unknown): string {
  // Entfernt alle nicht-druckbaren Zeichen (außer Standard-ASCII)
  return String(input).replaceAll(/[^\x20-\x7E]/g, '')
}
